import { Component, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FieldsetModule } from 'primeng/fieldset';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DropdownModule } from 'primeng/dropdown';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TooltipModule } from 'primeng/tooltip';
import { BusquedaMunicipioDialogComponent, MunicipioSeleccionado } from 'src/app/presentation/common/busquedas/busqueda-municipio-dialog.component';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';
import { Perfil } from 'src/app/domain/model/catalogo/perfil';
import { TipoAsesorReal } from 'src/app/domain/model/catalogo/tipo-asesor-real';
import { TipoFacturacion } from 'src/app/domain/model/catalogo/tipo-facturacion';
import { Proyecto } from 'src/app/domain/model/catalogo/proyecto';
import { Solucion } from 'src/app/domain/model/catalogo/solucion';
import { EmpleadoDetalle, EmpleadoRequest } from 'src/app/domain/model/empleado/empleado';
import { YES_VALUE, NO_VALUE } from 'src/app/common/constants-ejb';
import { sanitizeText, sanitizeEmail } from 'src/app/common/input-utils';

/**
 * Dumb component: "Datos de Persona" de "Modificar Empleado/Consultor"
 * (HU1259115).
 *
 * <p>Legacy: `ModificarEmpleadoViewImpl.ui.xml` líneas 62-287 — primera
 * sección del formulario. Orden verificado contra el `.ui.xml` (auditoría de
 * fidelidad, hallazgo A6): Identificación → Nombre Completo → Correo
 * Corporativo/Oficina → Usuario de Red → Tipo Empleado → Estrategia →
 * Sincronización Outlook → Perfil → Tipo Consultor/Programable → Tipo de
 * Facturación/Capacidad → Calcular Productividad → Municipios. Perfil, Tipo
 * Consultor, Programable, Tipo de Facturación, Capacidad, Estrategia
 * (Proyecto), Calcular Productividad, Solución y los 2 municipios viven
 * aquí, NO en "Datos Consultor" — son parte de "Datos de Persona" en el
 * legado, a pesar del nombre; solo se muestran condicionalmente cuando
 * `esAsesor()`.</p>
 *
 * <p>Foto Empleado / Modificar Foto (legado: `panelFoto`/`tbNombreFoto`+
 * `siSubirFoto`, primeros campos de esta misma sección, antes de
 * Identificación) — implementado: `panelFoto` se reemplaza por un
 * `<img>`/placeholder alimentado por `fotoUrl()`; `siSubirFoto` por un input
 * de archivo nativo oculto + botón, que valida formato/tamaño y emite el
 * `File` para que el facade lo suba (ver `ModificarEmpleadoFacade.seleccionarFoto()`).</p>
 */
@Component({
  selector: 'app-datos-persona-empleado',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FieldsetModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    DropdownModule,
    RadioButtonModule,
    TooltipModule,
    BusquedaMunicipioDialogComponent,
  ],
  template: `
    <p-fieldset legend="Datos de Persona" [toggleable]="true" styleClass="mb-4">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 sm:gap-x-6 gap-y-3">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Foto Empleado</label>
          @if (cargandoFoto()) {
            <i class="pi pi-spin pi-spinner text-2xl text-gray-400"></i>
          } @else if (fotoUrl()) {
            <img [src]="fotoUrl()" alt="Foto del empleado" class="w-28 h-36 object-cover rounded border border-gray-300 dark:border-gray-600" />
          } @else {
            <div class="w-28 h-36 flex items-center justify-center rounded border border-dashed border-gray-300 dark:border-gray-600 text-gray-400">
              <i class="pi pi-user text-4xl"></i>
            </div>
          }
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Identificación Empleado</label>
          <input pInputText readonly class="w-full"
            [value]="(detalle().cdtipoIdentificacion ?? '') + ' - ' + (detalle().nmidentificacion ?? '')"
            pTooltip="Identificación Empleado" />
          <!-- Legacy: saVerDetalle "Ver detalle Persona" — misma fila que Identificación Empleado en el .ui.xml. -->
          <p-button label="Ver detalle Persona" styleClass="p-button-text p-button-sm mt-1"
            [loading]="cargandoPersonaDetalle()" (onClick)="verDetallePersona.emit()"></p-button>
        </div>
        <div class="sm:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre Completo</label>
          <input pInputText readonly class="w-full" [value]="detalle().dsnombreComercial ?? ''" pTooltip="Nombre Completo" />
        </div>

        <div class="sm:col-span-2 lg:col-span-3">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Modificar Foto</label>
          <input type="file" #inputFoto accept=".jpg,.jpeg,.gif,.png" class="hidden" (change)="onFotoSeleccionada($event)" />
          <div class="flex flex-wrap items-center gap-2">
            <button pButton type="button" icon="pi pi-upload" label="Cambiar foto" class="p-button-outlined p-button-sm min-h-[44px]"
              (click)="inputFoto.click()"></button>
            @if (nombreFotoSeleccionada()) {
              <span class="text-sm text-gray-600 dark:text-gray-300 min-w-0 truncate">{{ nombreFotoSeleccionada() }}</span>
            }
          </div>
          @if (errorFoto()) {
            <small class="text-red-500 block mt-1">{{ errorFoto() }}</small>
          }
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Correo Corporativo</label>
          <input pInputText [(ngModel)]="form().dsemailCorporativo" class="w-full" maxlength="150" type="email"
            pTooltip="Correo Corporativo"
            (input)="form().dsemailCorporativo = sanitizeEmail(form().dsemailCorporativo)" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Oficina *</label>
          <p-dropdown [(ngModel)]="oficinaSeleccionada" [options]="oficinas()"
            optionLabel="descripcion" optionValue="codigo" placeholder="--Seleccione--"
            [filter]="true" filterBy="descripcion" appendTo="body" [style]="{ width: '100%' }"
            pTooltip="Oficina"></p-dropdown>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Usuario de Red *</label>
          <input pInputText [(ngModel)]="form().dsusuarioRed" class="w-full" maxlength="150"
            (input)="form().dsusuarioRed = sanitizeText(form().dsusuarioRed)" />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo Empleado *</label>
          <p-dropdown [(ngModel)]="form().cdtipoEmpleado" [options]="tiposEmpleado()"
            optionLabel="descripcion" optionValue="codigo" placeholder="--Seleccione--"
            [filter]="true" filterBy="descripcion" appendTo="body" [style]="{ width: '100%' }"
            pTooltip="Tipo de Empleado"
            (onChange)="cambioTipoEmpleado.emit()"></p-dropdown>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sincronización con Outlook</label>
          <div class="flex flex-wrap gap-4 pt-2">
            <div class="flex items-center">
              <p-radioButton name="snprogOutlook" [value]="YES" [(ngModel)]="form().snprogOutlook" inputId="outlookSi"></p-radioButton>
              <label for="outlookSi" class="ml-2 text-sm">Sí</label>
            </div>
            <div class="flex items-center">
              <p-radioButton name="snprogOutlook" [value]="NO" [(ngModel)]="form().snprogOutlook" inputId="outlookNo"></p-radioButton>
              <label for="outlookNo" class="ml-2 text-sm">No</label>
            </div>
          </div>
        </div>

        @if (esAsesor()) {
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Perfil</label>
            <p-dropdown [(ngModel)]="form().cdperfil" [options]="perfiles()"
              optionLabel="dsperfil" optionValue="cdperfil" placeholder="--Seleccione--"
              [filter]="true" filterBy="dsperfil" appendTo="body" [style]="{ width: '100%' }"
              pTooltip="Perfil"></p-dropdown>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo Consultor *</label>
            <p-dropdown [(ngModel)]="form().cdtipoAsesorReal" [options]="tiposAsesorReal()"
              optionLabel="dstipo" optionValue="cdtipoReal" placeholder="--Seleccione--"
              [filter]="true" filterBy="dstipo" appendTo="body" [style]="{ width: '100%' }"
              pTooltip="Tipo de Consultor"></p-dropdown>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo de Facturación *</label>
            <p-dropdown [(ngModel)]="form().cdtipoAsesor" [options]="tiposFacturacion()"
              optionLabel="dstipo" optionValue="cdtipo" placeholder="--Seleccione--"
              [filter]="true" filterBy="dstipo" appendTo="body" [style]="{ width: '100%' }"
              pTooltip="Tipo de Facturación"
              (onChange)="cambioTipoFacturacion.emit()"></p-dropdown>
          </div>
          @if (mostrarProgramable()) {
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Programable *</label>
              <div class="flex flex-wrap gap-4 pt-2">
                <div class="flex items-center">
                  <p-radioButton name="riesgosProg" [value]="YES" [(ngModel)]="form().snriesgosProgram" inputId="riesgosProgSi"></p-radioButton>
                  <label for="riesgosProgSi" class="ml-2 text-sm">Sí</label>
                </div>
                <div class="flex items-center">
                  <p-radioButton name="riesgosProg" [value]="NO" [(ngModel)]="form().snriesgosProgram" inputId="riesgosProgNo"></p-radioButton>
                  <label for="riesgosProgNo" class="ml-2 text-sm">No</label>
                </div>
              </div>
            </div>
          }
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Capacidad *</label>
            <p-inputNumber [(ngModel)]="form().nmcapacidad" class="w-full" [useGrouping]="false"
              [min]="0" [max]="999" styleClass="w-full" pTooltip="Capacidad del consultor"></p-inputNumber>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Estrategia *</label>
            <p-dropdown [(ngModel)]="form().nmconsecutivoProyecto" [options]="proyectos()"
              optionLabel="dsproyecto" optionValue="nmconsecutivo" placeholder="--Seleccione--"
              [filter]="true" filterBy="dsproyecto" appendTo="body" [style]="{ width: '100%' }"
              pTooltip="Proyecto al cual esta asignado el consultor"></p-dropdown>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Solución *</label>
            <p-dropdown [(ngModel)]="form().nmconsecutivoSolucion" [options]="soluciones()"
              optionLabel="dssolucion" optionValue="nmconsecutivo" placeholder="--Seleccione--"
              [filter]="true" filterBy="dssolucion" appendTo="body" [style]="{ width: '100%' }"></p-dropdown>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Calcular Productividad *</label>
            <div class="flex flex-wrap gap-4 pt-2">
              <div class="flex items-center">
                <p-radioButton name="calcProd" [value]="YES" [(ngModel)]="form().sncalcProd" inputId="calcProdSi" pTooltip="Calcular Productividad"></p-radioButton>
                <label for="calcProdSi" class="ml-2 text-sm">Sí</label>
              </div>
              <div class="flex items-center">
                <p-radioButton name="calcProd" [value]="NO" [(ngModel)]="form().sncalcProd" inputId="calcProdNo" pTooltip="Calcular Productividad"></p-radioButton>
                <label for="calcProdNo" class="ml-2 text-sm">No</label>
              </div>
            </div>
          </div>

          <div class="sm:col-span-2 lg:col-span-3">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Municipio Residencia *</label>
            <div class="flex gap-1 min-w-0">
              <input pInputText [value]="municipioResidenciaDisplay()" readonly class="flex-1 min-w-0" placeholder="Seleccione un municipio" />
              <button pButton icon="pi pi-search" class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Buscar municipio" (click)="dialogMunicipioResidenciaVisible = true"></button>
              <button pButton icon="pi pi-times" class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Limpiar" (click)="municipioResidenciaLimpiado.emit()" [disabled]="!municipioResidenciaDisplay()"></button>
            </div>
          </div>
          <div class="sm:col-span-2 lg:col-span-3">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Municipio Prestación *</label>
            <div class="flex gap-1 min-w-0">
              <input pInputText [value]="municipioPrestacionDisplay()" readonly class="flex-1 min-w-0" placeholder="Seleccione un municipio" />
              <button pButton icon="pi pi-search" class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Buscar municipio" (click)="dialogMunicipioPrestacionVisible = true"></button>
              <button pButton icon="pi pi-times" class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Limpiar" (click)="municipioPrestacionLimpiado.emit()" [disabled]="!municipioPrestacionDisplay()"></button>
            </div>
          </div>
        }
      </div>
    </p-fieldset>

    @if (dialogMunicipioResidenciaVisible) {
      <app-busqueda-municipio-dialog
        [titulo]="'Búsqueda de Municipio de Residencia'"
        (onSelect)="onMunicipioResidenciaSeleccionado($event)"
        (onClose)="dialogMunicipioResidenciaVisible = false">
      </app-busqueda-municipio-dialog>
    }
    @if (dialogMunicipioPrestacionVisible) {
      <app-busqueda-municipio-dialog
        [titulo]="'Búsqueda de Municipio de Prestación de Servicio'"
        (onSelect)="onMunicipioPrestacionSeleccionado($event)"
        (onClose)="dialogMunicipioPrestacionVisible = false">
      </app-busqueda-municipio-dialog>
    }
  `,
})
export class DatosPersonaEmpleadoComponent {
  readonly detalle = input.required<EmpleadoDetalle>();
  readonly form = input.required<EmpleadoRequest>();
  readonly esAsesor = input.required<boolean>();
  readonly mostrarProgramable = input.required<boolean>();
  readonly oficinas = input.required<CatalogoOption[]>();
  readonly tiposEmpleado = input.required<CatalogoOption[]>();
  readonly perfiles = input.required<Perfil[]>();
  readonly tiposAsesorReal = input.required<TipoAsesorReal[]>();
  readonly tiposFacturacion = input.required<TipoFacturacion[]>();
  readonly proyectos = input.required<Proyecto[]>();
  readonly soluciones = input.required<Solucion[]>();
  readonly municipioResidenciaDisplay = input.required<string>();
  readonly cargandoPersonaDetalle = input(false);
  readonly municipioPrestacionDisplay = input.required<string>();
  readonly fotoUrl = input<string | null>(null);
  readonly cargandoFoto = input(false);
  readonly errorFoto = input<string | null>(null);

  /** Legacy: `activity.validarCambioTipoAsesor(null, tipoEmpleado)` — el facade decide qué limpiar/sembrar. */
  readonly cambioTipoEmpleado = output<void>();
  /** Legacy: `addChangeHandlerTipoAsesor()` — el facade decide si oculta "Programable". */
  readonly cambioTipoFacturacion = output<void>();
  /** Legacy: `activity.verDetallePersona()` — el facade hace el fetch, el container abre el diálogo de solo lectura. */
  readonly verDetallePersona = output<void>();
  /** Legacy: `siSubirFoto` → `SubirFotoActivity` — el facade valida y codifica el archivo. */
  readonly fotoSeleccionada = output<File>();
  readonly municipioResidenciaSeleccionado = output<MunicipioSeleccionado>();
  readonly municipioResidenciaLimpiado = output<void>();
  readonly municipioPrestacionSeleccionado = output<MunicipioSeleccionado>();
  readonly municipioPrestacionLimpiado = output<void>();

  protected readonly YES = YES_VALUE;
  protected readonly NO = NO_VALUE;
  protected readonly sanitizeText = sanitizeText;
  protected readonly sanitizeEmail = sanitizeEmail;

  /** Visibilidad de los diálogos de búsqueda: estado de vista local, no lógica de negocio. */
  dialogMunicipioResidenciaVisible = false;
  dialogMunicipioPrestacionVisible = false;

  /** Legacy: `tbNombreFoto` — nombre del archivo elegido, solo texto de vista. */
  readonly nombreFotoSeleccionada = signal<string | null>(null);

  onFotoSeleccionada(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    this.nombreFotoSeleccionada.set(file.name);
    this.fotoSeleccionada.emit(file);
    input.value = '';
  }

  /**
   * Puente de tipo para el `p-dropdown` de Oficina: `form().nmconsecOficina`
   * es `number` (columna `NMCONSEC_OFICINA`, un `Long` en el backend), pero
   * `CatalogoOption.codigo` — el catálogo genérico que alimenta `oficinas()`,
   * compartido con catálogos de código alfabético como Tipo de Identificación
   * o Tipo de Empleado — es siempre `string`. PrimeNG compara el valor del
   * `ngModel` contra `option.codigo` con `===` (sin coerción): `10 === "10"`
   * es `false`, así que el dropdown nunca mostraba la oficina cargada, aunque
   * `form().nmconsecOficina` sí tuviera el valor correcto (bug real
   * reportado: "Oficina * no aparece llena"). Este getter/setter convierte
   * solo en el borde de este widget — `EmpleadoRequest.nmconsecOficina`
   * sigue siendo `number` en todo el resto del formulario y en el payload
   * que se envía al backend.
   */
  protected get oficinaSeleccionada(): string | undefined {
    const codigo = this.form().nmconsecOficina;
    return codigo != null ? String(codigo) : undefined;
  }

  protected set oficinaSeleccionada(codigo: string | undefined) {
    this.form().nmconsecOficina = codigo != null && codigo !== '' ? Number(codigo) : (undefined as unknown as number);
  }

  onMunicipioResidenciaSeleccionado(municipio: MunicipioSeleccionado): void {
    this.municipioResidenciaSeleccionado.emit(municipio);
    this.dialogMunicipioResidenciaVisible = false;
  }

  onMunicipioPrestacionSeleccionado(municipio: MunicipioSeleccionado): void {
    this.municipioPrestacionSeleccionado.emit(municipio);
    this.dialogMunicipioPrestacionVisible = false;
  }
}
